// src/pages/AcademicTranscript.jsx

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Card,
  Select,
  Table,
  Button,
  Alert,
  Spin,
  Collapse,
  Empty,
  Tag,
} from "antd";
import {
  fetchTranscriptByStudentId,
  clearTranscript,
  generateTranscript,
} from "../../store/studentAcademicSlice";
import { fetchStudentById } from "../../store/studentsSlice";
import useTokenExpiryCheck from "../../utils/UseTokenExpiryCheck";

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const AcademicTranscript = () => {
  useTokenExpiryCheck();
  const dispatch = useDispatch();

  const { fetchedTranscript, loading, error } = useSelector(
    (state) => state.transcript
  );
  const studentMap = useSelector((state) => state.students.byId);

  const stored = localStorage.getItem("userInfo");
  const { token, studentIds = [] } = stored
    ? JSON.parse(stored)
    : { token: null, studentIds: [] };

  const [selectedId, setSelectedId] = useState(studentIds[0] || "");

  useEffect(() => {
    if (studentIds?.length > 0) {
      studentIds.forEach((id) => {
        if (!studentMap[id]) {
          dispatch(fetchStudentById(id));
        }
      });
    }
  }, [dispatch, studentIds]);

  useEffect(() => {
    if (selectedId) {
      dispatch(fetchTranscriptByStudentId(selectedId));
    }
  }, [dispatch, selectedId]);

  useEffect(() => {
    return () => dispatch(clearTranscript());
  }, [dispatch]);

  const handleGenerate = () => {
    if (token && selectedId) dispatch(generateTranscript(selectedId));
  };

  const getStudentName = (id) => {
    const student = studentMap[id];
    if (!student || !student.basicPersonalInformation) return id;
    const info = student.basicPersonalInformation;
    return `${info.firstName || ""} ${info.surName || ""} ${info.lastName || ""}`.trim();
  };

  const columns = [
    {
      title: "Subject",
      dataIndex: "subjectName",
      key: "subjectName",
    },
    {
      title: "Mark",
      dataIndex: "mark",
      key: "mark",
      render: (mark) => <Tag color={mark >= 50 ? "green" : "red"}>{mark}</Tag>,
    }
  ];

  const groupByTermBatch = (transcripts) => {
    return transcripts.reduce((acc, cur) => {
      const key = `${cur.termName} - ${cur.batchName}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(cur);
      return acc;
    }, {});
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <Card bordered={false} className="shadow-lg">
        <Title level={2}>Academic Transcript</Title>

        {!token ? (
          <Alert
            message="You are not logged in. Please log in to continue."
            type="error"
            showIcon
            className="my-4"
          />
        ) : studentIds.length === 0 ? (
          <Alert
            message="No student IDs found for your account."
            type="error"
            showIcon
            className="my-4"
          />
        ) : (
          <>
            {studentIds.length > 1 && (
              <div className="my-4">
                <Text strong>Select Your Child:</Text>
                <Select
                  value={selectedId}
                  onChange={setSelectedId}
                  style={{ width: 300, marginLeft: 10 }}
                >
                  {studentIds.map((id) => (
                    <Option key={id} value={id}>
                      {getStudentName(id)}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                className="my-4"
              />
            )}

            <Button
              type="primary"
              onClick={handleGenerate}
              disabled={!selectedId || loading}
              loading={loading}
              className="my-4"
            >
              Generate Transcript
            </Button>

            {loading && <Spin size="large" className="mt-6" />}

            {!loading && fetchedTranscript?.length > 0 ? (
              <Collapse accordion className="mt-6">
                {fetchedTranscript.map((yearData, index) => {
                  const grouped = groupByTermBatch(yearData.transcripts);

                  return (
                    <Panel
                      header={`Academic Year: ${yearData.academicYear}`}
                      key={index}
                    >
                      <Collapse accordion>
                        {Object.entries(grouped).map(
                          ([termBatchKey, transcripts]) => (
                            <Panel
                              header={`Term & Batch: ${termBatchKey}`}
                              key={termBatchKey}
                            >
                              <Table
                                columns={columns}
                                dataSource={transcripts}
                                rowKey={(record, idx) =>
                                  `${record.subjectName}-${record.batchName}-${idx}`
                                }
                                pagination={false}
                                bordered
                                size="middle"
                              />
                            </Panel>
                          )
                        )}
                      </Collapse>
                    </Panel>
                  );
                })}
              </Collapse>
            ) : (
              !loading && (
                <Empty
                  description="No transcript data found"
                  className="mt-10"
                />
              )
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default AcademicTranscript;
